'use client';

import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { ArrowRightShortIcon } from '../icons';
import { cn } from '../utils';

function Breadcrumb(props: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-3 font-medium break-words text-base h-[43px]',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-2', className)}
      {...props}
    />
  );
}

type BreadcrumbLinkProps = React.ComponentPropsWithoutRef<'a'> & {
  asChild?: boolean;
  id: string;
};

type BreadcrumbWithIconProps<T> = T & {
  icon: React.ReactNode;
  active?: boolean;
};

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(
  ({ asChild, className, id, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          id={id}
          data-slot="breadcrumb-link"
          className={cn(
            'hover:underline transition-colors cursor-pointer',
            className,
          )}
          {...props}
        />
      );
    }

    return (
      <a
        ref={ref}
        id={id}
        data-slot="breadcrumb-link"
        className={cn(
          'hover:underline transition-colors cursor-pointer',
          className,
        )}
        {...props}
      />
    );
  },
);
BreadcrumbLink.displayName = 'BreadcrumbLink';

const BreadcrumbLinkWithIcon = React.forwardRef<
  HTMLAnchorElement,
  BreadcrumbWithIconProps<BreadcrumbLinkProps>
>(({ icon, className, children, active, ...props }, ref) => {
  const isActive =
    active ??
    (props['aria-current'] === 'page' || props['aria-current'] === true);

  return (
    <BreadcrumbLink
      ref={ref}
      className={cn('inline-flex items-center gap-2', className)}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center gap-2 text-muted-foreground transition-colors [&>svg]:size-5',
          isActive && 'text-primary',
        )}
      >
        {icon}
      </span>
      <span>{children}</span>
    </BreadcrumbLink>
  );
});
BreadcrumbLinkWithIcon.displayName = 'BreadcrumbLinkWithIcon';

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground', className)}
      {...props}
    />
  );
}

function BreadcrumbPageWithIcon({
  icon,
  className,
  children,
  active,
  ...props
}: BreadcrumbWithIconProps<React.ComponentPropsWithoutRef<'span'>>) {
  return (
    <BreadcrumbPage
      className={cn('inline-flex items-center gap-2', className)}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center gap-2 text-muted-foreground transition-colors [&>svg]:size-5',
          active && 'text-primary',
        )}
      >
        {icon}
      </span>
      <span>{children}</span>
    </BreadcrumbPage>
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-5', className)}
      {...props}
    >
      {children ?? <ArrowRightShortIcon />}
    </li>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbLinkWithIcon,
  BreadcrumbPage,
  BreadcrumbPageWithIcon,
  BreadcrumbSeparator,
};
